import { createClient } from "@sanity/client";

const projectId =
  process.env.REACT_APP_SANITY_PROJECT_ID || "s7eia0gv";
const dataset = process.env.REACT_APP_SANITY_DATASET || "production";

// Browser-safe client for published (CDN-cached) content.
// No token is sent to the browser — published datasets are public.
// Sending a token to the browser would expose it to anyone who downloads
// the JS bundle and is unnecessary for reading published content.
export const client = createClient({
  projectId,
  dataset,
  apiVersion: "2024-12-01",
  useCdn: true,
});

// Preview client is only created when a preview token is provided via env.
// In the browser this means no preview token = no preview client (read-only
// published content only). On the server / during SSR you can still wire up
// a token if you need to render drafts.
const previewToken = process.env.REACT_APP_SANITY_PREVIEW_TOKEN;
const isBrowser = typeof window !== "undefined";

export const previewClient = previewToken
  ? createClient({
      projectId,
      dataset,
      apiVersion: "2024-12-01",
      useCdn: false,
      perspective: "previewDrafts",
      token: previewToken,
    })
  : null;

const PREVIEW_PARAM = "preview";
const PREVIEW_SECRET = previewToken;

export function isPreviewMode() {
  if (isBrowser && previewToken) {
    const params = new URLSearchParams(window.location.search);
    return params.get(PREVIEW_PARAM) === previewToken;
  }
  // Server-side: opt-in via REACT_APP_SANITY_PREVIEW_ENABLED or the standard
  // Sanity Studio convention (request includes perspective: "previewDrafts").
  return !isBrowser && previewToken != null;
}

export function getActiveClient() {
  if (previewClient && isPreviewMode()) return previewClient;
  return client;
}

// For write operations, use a server API endpoint.
export const writeClient = {
  create: async (document) => {
    try {
      const apiBaseUrl =
        process.env.NODE_ENV === "production"
          ? "/api/sanity/create"
          : `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/sanity/create`;

      const response = await fetch(apiBaseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error("Error creating document:", error);
      throw error;
    }
  },

  patch: (documentId) => ({
    set: (data) => ({
      commit: async () => {
        try {
          const apiBaseUrl =
            process.env.NODE_ENV === "production"
              ? "/api/sanity/patch"
              : `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/sanity/patch`;

          const response = await fetch(apiBaseUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documentId, operations: { set: data } }),
          });
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          return response.json();
        } catch (error) {
          console.error("Error setting document fields:", error);
          throw error;
        }
      },
    }),
    setIfMissing: (data) => ({
      inc: (incData) => ({
        commit: async () => {
          try {
            const apiBaseUrl =
              process.env.NODE_ENV === "production"
                ? "/api/sanity/patch"
                : `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/sanity/patch`;

            const response = await fetch(apiBaseUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                documentId,
                operations: { setIfMissing: data, inc: incData },
              }),
            });
            if (!response.ok) {
              throw new Error(`API error: ${response.status}`);
            }
            return response.json();
          } catch (error) {
            console.error("Error patching document:", error);
            throw error;
          }
        },
      }),
      commit: async () => {
        try {
          const apiBaseUrl =
            process.env.NODE_ENV === "production"
              ? "/api/sanity/patch"
              : `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/sanity/patch`;

          const response = await fetch(apiBaseUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              documentId,
              operations: { setIfMissing: data },
            }),
          });
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          return response.json();
        } catch (error) {
          console.error("Error patching document fields:", error);
          throw error;
        }
      },
    }),
  }),
};

// Posts are assumed to be tagged with a `language` field (e.g. "sk", "en",
// "pl", "cs"). The locale-aware query restricts the result to the requested
// language so we never accidentally render a Slovak post on a Czech page.
const POST_PROJECTION = `{
  _id,
  title,
  excerpt,
  slug,
  language,
  localeVariants[]{
    ...,
    slug,
    seo,
    body[]{...}
  },
  mainImage{
    asset->,
    caption
  },
  body[]{
    ...,
    _type == "block" => {
      ...,
      markDefs[]{
        ...,
        _type == "link" => {
          "href": href
        }
      }
    },
    _type == "image" => {
      ...,
      asset->
    },
    _type == "pollReference" => {
      ...,
      _ref,
      _type
    },
    _type == "customTweet" => {
      ...,
      author {
        ...,
        avatar {
          asset->
        }
      }
    }
  },
  publishedAt,
  category,
  tags,
  likes,
  comments,
  author->{
    name,
    image
  }
}`;

// Backwards-compatible: returns the first post matching the slug regardless
// of language. Prefer `getPostInLocale(slug, locale)` for new code.
export async function getPost(slug) {
  return await client.fetch(
    `*[_type == "post" && slug.current == $slug][0]${POST_PROJECTION}`,
    { slug }
  );
}

// Locale-aware post lookup. Falls back to the default locale if the requested
// locale doesn't exist, so a missing translation doesn't 404.
export async function getPostInLocale(slug, locale, fallbackLocale = "sk") {
  const primary = await getActiveClient().fetch(
    `*[_type == "post" && slug.current == $slug && language == $locale][0]${POST_PROJECTION}`,
    { slug, locale }
  );

  if (primary) return primary;

  if (locale !== fallbackLocale) {
    return await getActiveClient().fetch(
      `*[_type == "post" && slug.current == $slug && language == $fallbackLocale][0]${POST_PROJECTION}`,
      { slug, fallbackLocale }
    );
  }

  return null;
}